import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, RefreshControl, Alert } from 'react-native';
import { Text, Card, Searchbar, ActivityIndicator, Chip, Divider, Menu, IconButton } from 'react-native-paper';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../../contexts/AuthContext';
import productosApi from '../../services/productosApi';
import BarcodeScannerModal from '../../components/BarcodeScannerModal';
import apiService from '../../services/api';
import { consumeInventoryDirty } from '../../utils/inventoryRefresh';
const api = productosApi;

// Aplica filtro de tipo + búsqueda a la lista. Centralizado para reusarlo tanto
// en la UI como al recargar en segundo plano (y no perder el filtro/búsqueda).
const filtrarProductos = (data, type, query) => {
  let filtered = [...data];
  if (type === 'bajo_stock') filtered = filtered.filter((p) => p.stock_total < 20);
  else if (type === 'alto_stock') filtered = filtered.filter((p) => p.stock_total >= 100);
  const q = (query || '').trim().toLowerCase();
  if (q) {
    filtered = filtered.filter(
      (p) =>
        (p.nombre && p.nombre.toLowerCase().includes(q)) ||
        (p.codigo && p.codigo.toLowerCase().includes(q)) ||
        (p.marca && p.marca.toLowerCase().includes(q))
    );
  }
  return filtered;
};

const InventarioScreen = () => {
  const navigation = useNavigation();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const [searchQuery, setSearchQuery] = useState('');
  const [productos, setProductos] = useState([]);
  const [filteredProductos, setFilteredProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterType, setFilterType] = useState('todos');
  // Refs con el filtro/búsqueda vigentes para poder re-aplicarlos al recargar
  // en segundo plano (loadProductos no ve el estado actual por el closure).
  const searchRef = useRef('');
  const filterRef = useRef('todos');
  useEffect(() => { searchRef.current = searchQuery; }, [searchQuery]);
  useEffect(() => { filterRef.current = filterType; }, [filterType]);
  const [menuVisible, setMenuVisible] = useState(false);
  const [selectedProducto, setSelectedProducto] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [showScanner, setShowScanner] = useState(false);

  const loadProductos = useCallback(async () => {
    // Solo intentar cargar datos si el usuario está autenticado
    if (!user) {
      setLoading(false);
      setRefreshing(false);
      return;
    }
    
    try {
      // Solo mostrar spinner full-screen en la carga inicial (sin productos).
      // Si ya hay datos en pantalla, recargamos en silencio para preservar
      // el scroll del usuario al volver desde un detalle.
      if (productos.length === 0) {
        setLoading(true);
      }

      // Llamada a la API real para obtener los productos
      const productosData = await api.getProductos();
      console.log('Productos cargados desde la API:', productosData);
      
      // Asegurarse de que cada producto tenga unidades
      const productosConUnidades = productosData.map(producto => {
        // Depurar información de categoría
        console.log('Datos del producto:', producto.id, producto.nombre);
        console.log('Categoría:', producto.categoria_id, producto.categoria_nombre);
        
        return {
          ...producto,
          unidades: producto.unidades || [],
          stock_total: producto.stock_total || 0
        };
      });
      
      setProductos(productosConUnidades);
      // Re-aplicar el filtro y la búsqueda vigentes para NO perder la posición
      // del usuario al volver de un detalle (antes mostraba toda la lista = "A").
      setFilteredProductos(filtrarProductos(productosConUnidades, filterRef.current, searchRef.current));
    } catch (error) {
      console.error('Error al cargar productos:', error);
      // Si hay un error, mostramos el mensaje en la consola sin interrumpir la experiencia
      console.log('Error al cargar los productos:', error.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);

  // Recargar SOLO en la primera carga o cuando algo cambió (crear/editar/borrar).
  // Al volver de ver un producto NO se recarga, para conservar scroll/posición,
  // filtro y búsqueda (antes volvía siempre al inicio de la lista).
  useFocusEffect(
    useCallback(() => {
      if (!user) return;
      if (productos.length === 0 || consumeInventoryDirty()) {
        loadProductos();
      }
    }, [user, loadProductos, productos.length])
  );
  
  useEffect(() => {
    // Configurar opciones de navegación - solo mostrar botón de agregar si no es vendedor
    navigation.setOptions({
      headerRight: () => (
        user?.rol !== 'vendedor' ? (
          <TouchableOpacity
            style={{ marginRight: 16 }}
            onPress={() => navigation.navigate('NuevoProducto')}
          >
            <Ionicons name="add" size={24} color="#007bff" />
          </TouchableOpacity>
        ) : null
      ),
    });
  }, [navigation, user]);

  const onRefresh = () => {
    setRefreshing(true);
    loadProductos();
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      applyFilters(filterType);
    } else {
      const searchLower = query.toLowerCase();
      const filtered = productos.filter(
        (producto) =>
          (producto.nombre && producto.nombre.toLowerCase().includes(searchLower)) ||
          (producto.codigo && producto.codigo.toLowerCase().includes(searchLower)) ||
          (producto.marca && producto.marca.toLowerCase().includes(searchLower))
      );
      setFilteredProductos(filtered);
    }
  };

  const applyFilters = (type) => {
    setFilterType(type);
    let filtered = [...productos];

    switch (type) {
      case 'bajo_stock':
        filtered = filtered.filter((producto) => producto.stock_total < 20);
        break;
      case 'alto_stock':
        filtered = filtered.filter((producto) => producto.stock_total >= 100);
        break;
      default:
        // 'todos' - no filter
        break;
    }

    if (searchQuery.trim() !== '') {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (producto) =>
          (producto.nombre && producto.nombre.toLowerCase().includes(searchLower)) ||
          (producto.codigo && producto.codigo.toLowerCase().includes(searchLower)) ||
          (producto.marca && producto.marca.toLowerCase().includes(searchLower))
      );
    }

    setFilteredProductos(filtered);
  };

  const handleDeleteProducto = (producto) => {
    Alert.alert(
      'Confirmar eliminación',
      `¿Estás seguro de que deseas eliminar el producto "${producto.nombre}"?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        { 
          text: 'Eliminar', 
          style: 'destructive',
          onPress: async () => {
            try {
              setLoading(true);
              await api.deleteProducto(producto.id);
              // Actualizar la lista de productos después de eliminar
              const updatedProductos = productos.filter(p => p.id !== producto.id);
              setProductos(updatedProductos);
              setFilteredProductos(updatedProductos.filter(p => {
                // Mantener los filtros actuales
                if (filterType === 'bajo_stock' && p.stock_total >= 20) return false;
                if (filterType === 'alto_stock' && p.stock_total < 100) return false;
                if (searchQuery.trim() !== '') {
                  const searchLower = searchQuery.toLowerCase();
                  return (p.nombre && p.nombre.toLowerCase().includes(searchLower)) ||
                         (p.codigo && p.codigo.toLowerCase().includes(searchLower));
                }
                return true;
              }));
              Alert.alert('Éxito', 'Producto eliminado correctamente');
            } catch (error) {
              console.error('Error al eliminar producto:', error);
              Alert.alert('Error', 'No se pudo eliminar el producto: ' + (error.message || 'Error desconocido'));
            } finally {
              setLoading(false);
            }
          } 
        }
      ]
    );
  };

  const renderProductoItem = ({ item }) => (
    <Card style={styles.card}>
      <Card.Content>
        <View style={styles.productHeader}>
          <TouchableOpacity 
            style={{flex: 1}}
            onPress={() => navigation.navigate('ProductoDetalle', { productoId: item.id })}
          >
            <View>
              <Text style={styles.productName}>{item.nombre || 'Sin nombre'}</Text>
              {item.marca ? (
                <Text style={styles.productCode}>Marca: {item.marca} | Código: {item.codigo || 'N/A'}</Text>
              ) : (
                <Text style={styles.productCode}>Código: {item.codigo || 'N/A'}</Text>
              )}
            </View>
          </TouchableOpacity>
          <View style={styles.actionButtons}>
            {item.unidades && item.unidades.length > 0 && item.unidades.find(u => u.es_unidad_base) && (
              <Text style={styles.productPrice}>
                L.{parseFloat(item.unidades.find(u => u.es_unidad_base).precio || 0).toFixed(2)}
              </Text>
            )}
            <IconButton
              icon="dots-vertical"
              size={20}
              onPress={(event) => {
                const { pageX, pageY } = event.nativeEvent;
                setMenuPosition({ x: pageX, y: pageY });
                setSelectedProducto(item);
                setMenuVisible(true);
              }}
            />
          </View>
        </View>
        <Divider style={styles.divider} />
        <TouchableOpacity 
          onPress={() => navigation.navigate('ProductoDetalle', { productoId: item.id })}
        >
          <View style={styles.productFooter}>
            <Text>Categoría: {item.categoria_nombre || 'Sin categoría'}</Text>
            <View style={[
              styles.stockBadge,
              item.stock_total < 20 ? styles.lowStock : 
              item.stock_total >= 100 ? styles.highStock : styles.mediumStock
            ]}>
              <Text style={styles.stockText}>Stock: {item.stock_total || 0} {item.unidad_principal}</Text>
            </View>
          </View>
        </TouchableOpacity>
      </Card.Content>
      {menuVisible && selectedProducto && selectedProducto.id === item.id && (
        <Menu
          visible={true}
          onDismiss={() => setMenuVisible(false)}
          anchor={menuPosition}
        >
        <Menu.Item
          onPress={() => {
            setMenuVisible(false);
            navigation.navigate('ProductoDetalle', { productoId: item.id });
          }}
          title="Ver detalles"
          leadingIcon="eye"
        />
        {user?.rol !== 'vendedor' && (
          <Menu.Item
            onPress={() => {
              setMenuVisible(false);
              navigation.navigate('EditarProducto', { producto: item });
            }}
            title="Editar"
            leadingIcon="pencil"
          />
        )}
        {user?.rol !== 'vendedor' && (
          <Menu.Item
            onPress={() => {
              setMenuVisible(false);
              handleDeleteProducto(item);
            }}
            title="Eliminar"
            leadingIcon="delete"
          />
        )}
        </Menu>
      )}
    </Card>
  );

  return (
    <View style={styles.container}>
      <Searchbar
        placeholder="Buscar producto por nombre o código"
        onChangeText={handleSearch}
        value={searchQuery}
        style={styles.searchBar}
      />

      <View style={styles.filterContainer}>
        {[
          { id: 'todos', label: 'Todos' },
          { id: 'bajo_stock', label: 'Bajo stock' },
          { id: 'alto_stock', label: 'Alto stock' },
        ].map(f => (
          <Chip
            key={f.id}
            onPress={() => applyFilters(f.id)}
            style={[styles.filterChip, filterType === f.id && styles.filterChipActive]}
            textStyle={filterType === f.id ? styles.filterChipTextActive : styles.filterChipTextInactive}
          >
            {f.label}
          </Chip>
        ))}
      </View>

      {/* Acciones rápidas de inventario */}
      <View style={styles.quickActions}>
        <TouchableOpacity style={[styles.quickBtn, { backgroundColor: '#E8D5F5' }]} onPress={() => setShowScanner(true)}>
          <Ionicons name="barcode" size={18} color="#6F42C1" />
          <Text style={[styles.quickBtnText, { color: '#6F42C1' }]}>Escanear</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.quickBtn, { backgroundColor: '#FFE8CC' }]} onPress={() => navigation.navigate('Lotes')}>
          <Ionicons name="calendar" size={18} color="#FD7E14" />
          <Text style={[styles.quickBtnText, { color: '#FD7E14' }]}>Lotes</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.quickBtn, { backgroundColor: '#D1ECF1' }]} onPress={() => navigation.navigate('ConteoInventario')}>
          <Ionicons name="clipboard" size={18} color="#0C5460" />
          <Text style={[styles.quickBtnText, { color: '#0C5460' }]}>Conteo</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.quickBtn, { backgroundColor: '#F8D7DA' }]} onPress={() => navigation.navigate('BajoStock')}>
          <Ionicons name="alert-circle" size={18} color="#DC3545" />
          <Text style={[styles.quickBtnText, { color: '#DC3545' }]}>Bajo Stock</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.quickBtn, { backgroundColor: '#E2E3E5' }]} onPress={() => navigation.navigate('Categorias')}>
          <Ionicons name="pricetags" size={18} color="#383D41" />
          <Text style={[styles.quickBtnText, { color: '#383D41' }]}>Categorías</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.quickBtn, { backgroundColor: '#D4EDDA' }]} onPress={() => navigation.navigate('Presentaciones')}>
          <Ionicons name="cube" size={18} color="#155724" />
          <Text style={[styles.quickBtnText, { color: '#155724' }]}>Presentaciones</Text>
        </TouchableOpacity>
      </View>

      <BarcodeScannerModal
        visible={showScanner}
        onClose={() => setShowScanner(false)}
        onScanned={async (codigo) => {
          setShowScanner(false);
          try {
            const producto = await apiService.buscarPorCodigoBarras(codigo);
            if (producto && producto.id) {
              navigation.navigate('ProductoDetalle', { productoId: producto.id });
            }
          } catch (error) {
            Alert.alert('No Encontrado', `No se encontro producto con codigo: ${codigo}`);
          }
        }}
      />

      {loading ? (
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" color="#0066cc" />
          <Text style={styles.loaderText}>Cargando inventario...</Text>
        </View>
      ) : (
        <>
          <FlatList
            data={filteredProductos}
            renderItem={renderProductoItem}
            keyExtractor={(item) => item.id.toString()}
            contentContainerStyle={styles.listContainer}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
            }
            ListEmptyComponent={
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>No se encontraron productos</Text>
              </View>
            }
          />

          {user?.rol !== 'vendedor' && (
            <TouchableOpacity
              style={[styles.fab, { bottom: 20 + insets.bottom }]}
              onPress={() => navigation.navigate('NuevoProducto')}
            >
              <Ionicons name="add" size={24} color="white" />
            </TouchableOpacity>
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 10,
  },
  searchBar: {
    marginBottom: 10,
    backgroundColor: 'white',
  },
  quickActions: {
    flexDirection: 'row',
    marginBottom: 10,
    justifyContent: 'space-between',
  },
  quickBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    borderRadius: 8,
    marginHorizontal: 2,
  },
  quickBtnText: {
    fontSize: 11,
    fontWeight: 'bold',
    marginLeft: 4,
  },
  filterContainer: {
    flexDirection: 'row',
    marginBottom: 10,
  },
  filterChip: {
    marginRight: 8,
    backgroundColor: '#f0f0f0',
  },
  filterChipActive: {
    backgroundColor: '#4CAF50',
  },
  filterChipTextInactive: {
    color: '#333',
  },
  filterChipTextActive: {
    color: 'white',
    fontWeight: '600',
  },
  card: {
    marginBottom: 10,
    elevation: 2,
  },
  productHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  productName: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  productCode: {
    fontSize: 12,
    color: '#666',
  },
  productPrice: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0066cc',
  },
  divider: {
    marginVertical: 8,
  },
  productFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stockBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  lowStock: {
    backgroundColor: '#ffcccc',
  },
  mediumStock: {
    backgroundColor: '#ffffcc',
  },
  highStock: {
    backgroundColor: '#ccffcc',
  },
  stockText: {
    fontSize: 12,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loaderText: {
    marginTop: 10,
  },
  listContainer: {
    paddingBottom: 80,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 50,
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
  },
  fab: {
    position: 'absolute',
    right: 20,
    bottom: 20,
    backgroundColor: '#0066cc',
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 4,
  },
});

export default InventarioScreen;
